import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { SequentialRater } from "~/components/wheel/sequential-rater";
import { Wheel } from "~/components/wheel/wheel";
import { createWheelSessionFn } from "~/lib/functions/wheel";
import { trackWheelCompleted } from "~/lib/monitoring/track-event";
import {
  coachLeadLinkMessage,
  type CoachLeadLinkProblem,
} from "~/lib/shared/coach-leads";
import { createBrandMeta } from "~/lib/shared/meta";
import { parseEngagementSearch } from "~/lib/shared/wheel-request";
import { buildWheelJsonLd } from "~/lib/wheel/content";
import {
  loadWheelStateForEdit,
  setWheelLabel,
  setWheelScore,
  setWheelSessionId,
  useWheelState,
} from "~/lib/wheel/state";
import { WheelOfLifeGuide } from "~/routes/_public/wheel-of-life/-components/wheel-of-life-guide";

const REVEAL_AFTER_TOUCHES = 4;

export const Route = createFileRoute("/_public/wheel-of-life/")({
  staticData: { title: "Wheel of Life Assessment" },
  head: () => ({
    ...createBrandMeta({
      title:
        "Wheel of Life Assessment — Free Online Tool & Complete Guide | Dream Coach Match",
      description:
        "Score 8 life areas in 2 minutes and see the shape of your life. Free Wheel of Life assessment with a personalized reflection. No signup required.",
      ogTitle: "Wheel of Life Assessment — Free Online Tool & Complete Guide",
      ogDescription:
        "Score 8 life areas. Get a 2-minute personalized reflection on where your life is thriving — and where it's quietly stuck.",
      ogImage: "/og/wheel-of-life-generic.png",
      ogType: "website",
      twitterCard: "summary_large_image",
      canonical: "https://dreamcoachmatch.com/wheel-of-life",
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(buildWheelJsonLd()),
      },
    ],
  }),
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    from?: string;
    link?: CoachLeadLinkProblem;
    engagement?: string;
  } => {
    const engagement = parseEngagementSearch(search.engagement);
    return {
      ...(typeof search.from === "string" && { from: search.from }),
      // A coach Wheel "Save my results" link that did not work.
      ...((search.link === "expired" || search.link === "invalid") && {
        link: search.link,
      }),
      ...(engagement && { engagement }),
    };
  },
  // A coach's request link: the client signs in first, so the result can be
  // shared from their account.
  beforeLoad: async ({ search, location }) => {
    if (!search.engagement) return;
    const { requireAuth } = await import("~/lib/functions/require-auth");
    await requireAuth(location);
  },
  component: WheelOfLifeIndex,
});

function WheelOfLifeIndex() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const state = useWheelState();
  const [submitting, setSubmitting] = useState(false);
  // Synchronous double-submit guard. `submitting` state is async/batched, so two
  // clicks in the same tick can both read it as false; a ref flips immediately.
  const submittingRef = useRef(false);
  const [currentIdx, setCurrentIdx] = useState(0);

  useEffect(() => {
    if (!search.from) return;
    const sessionId = search.from;
    let cancelled = false;
    (async () => {
      try {
        const { getWheelSessionFn } = await import("~/lib/functions/wheel");
        const row = await getWheelSessionFn({ data: { id: sessionId } });
        if (cancelled || !row) return;
        loadWheelStateForEdit({ labels: row.labels, scores: row.scores });
      } catch (err) {
        console.error("Failed to load wheel session for re-scoring:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [search.from]);

  const touchedCount = useMemo(
    () => state.touchedMask.filter(Boolean).length,
    [state.touchedMask],
  );
  const allTouched = touchedCount === state.touchedMask.length;
  const ctaReady = touchedCount >= REVEAL_AFTER_TOUCHES;
  const remaining = Math.max(0, REVEAL_AFTER_TOUCHES - touchedCount);

  const handleScore = useCallback((index: number, score: number) => {
    setWheelScore(index, score);
  }, []);

  const handleAdvance = useCallback(() => {
    setCurrentIdx((prev) => {
      // Find next unscored area after prev; if none, go to next index.
      const len = state.touchedMask.length;
      for (let step = 1; step <= len; step++) {
        const candidate = (prev + step) % len;
        if (!state.touchedMask[candidate]) return candidate;
      }
      return Math.min(len - 1, prev + 1);
    });
  }, [state.touchedMask]);

  const handleBack = useCallback(() => {
    setCurrentIdx((prev) => Math.max(0, prev - 1));
  }, []);

  const handleWedgeFocus = useCallback((index: number) => {
    setCurrentIdx(index);
  }, []);

  const handleSubmit = async () => {
    // Guard against double-submit — the rater's "See reflection →" button and
    // the EarnedCta both call this, and either can be fired twice in quick
    // succession before navigation kicks in. The ref blocks synchronously.
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const { id } = await createWheelSessionFn({
        data: {
          labels: state.labels,
          scores: state.scores,
          source: undefined,
        },
      });
      trackWheelCompleted("public");
      setWheelSessionId(id);
      navigate({
        to: "/wheel-of-life/results/$id",
        params: { id },
        search: { claim: undefined, engagement: search.engagement },
      });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div
      className="relative isolate min-h-screen"
      style={{
        background: `
          radial-gradient(ellipse 70% 45% at 50% -5%, oklch(0.96 0.05 60 / 0.7) 0%, transparent 60%),
          radial-gradient(ellipse 80% 35% at 85% 105%, oklch(0.94 0.06 240 / 0.45) 0%, transparent 55%),
          oklch(0.99 0.005 80)
        `,
      }}
    >
      <main className="mx-auto max-w-4xl px-4 pt-8 pb-20 sm:pt-10">
        {search.link && (
          <p
            role="status"
            className="mx-auto mb-6 max-w-xl rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-amber-200"
          >
            {coachLeadLinkMessage(search.link, null)}
          </p>
        )}
        {/* eyebrow + headline */}
        <header className="text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-100/70 px-3 py-1 text-[11px] font-medium tracking-[0.14em] text-orange-800 uppercase ring-1 ring-orange-200/60">
            <span
              aria-hidden
              className="h-1.5 w-1.5 rounded-full bg-orange-500"
            />
            Free interactive self-assessment
          </span>
          <h1 className="mt-4 text-3xl leading-[1.1] font-medium tracking-[-0.02em] text-neutral-900 sm:text-4xl">
            Wheel of Life Assessment
          </h1>
          <p className="mx-auto mt-3 max-w-[34ch] text-lg leading-snug font-medium text-neutral-700 sm:text-xl">
            See where your life is thriving —{" "}
            <span className="text-neutral-400">
              and where it's quietly stuck.
            </span>
          </p>
          <p className="mx-auto mt-3 max-w-[44ch] text-sm leading-relaxed text-neutral-600 sm:text-base">
            A 2-minute self-check across 8 life areas, with a personalized read
            of your results.
          </p>
        </header>

        {/* sequential rater — in a soft card so it reads as the primary action */}
        <section className="mt-6 flex justify-center">
          <div className="w-full max-w-xl rounded-2xl bg-white/70 px-4 py-5 shadow-[0_1px_2px_rgba(0,0,0,0.03),0_8px_24px_-12px_rgba(0,0,0,0.06)] ring-1 ring-neutral-200/70 backdrop-blur-sm sm:px-6 sm:py-6">
            <SequentialRater
              labels={state.labels}
              scores={state.scores}
              touchedMask={state.touchedMask}
              currentIdx={currentIdx}
              onScore={handleScore}
              onAdvance={handleAdvance}
              onBack={handleBack}
              onComplete={handleSubmit}
              submitting={submitting}
            />
          </div>
        </section>

        {/* wheel */}
        <section className="mt-6 flex justify-center">
          <Wheel
            mode="interactive"
            labels={state.labels}
            scores={state.scores}
            touchedMask={state.touchedMask}
            spotlightIndex={currentIdx}
            onScoreChange={handleScore}
            onLabelChange={setWheelLabel}
            onWedgeFocus={handleWedgeFocus}
          />
        </section>

        {/* CTA — disabled vs earned */}
        <div className="mt-6 flex flex-col items-center">
          {ctaReady ? (
            <EarnedCta
              submitting={submitting}
              allTouched={allTouched}
              onClick={handleSubmit}
            />
          ) : (
            <div className="text-center">
              <p className="text-sm text-neutral-500">
                Score{" "}
                <span className="font-medium text-neutral-700">
                  {remaining} more area{remaining === 1 ? "" : "s"}
                </span>{" "}
                to unlock your reflection.
              </p>
            </div>
          )}
        </div>

        {/* Print-first alternative */}
        <p className="mt-6 text-center text-sm text-neutral-500">
          Prefer pen and paper?{" "}
          <Link
            to="/wheel-of-life/template"
            className="font-medium text-orange-700 underline decoration-orange-300 underline-offset-2 transition-colors hover:text-orange-800"
          >
            Download the printable template →
          </Link>
        </p>

        {/* methodology + guide + FAQ */}
        <WheelOfLifeGuide />
      </main>
    </div>
  );
}

function EarnedCta({
  submitting,
  allTouched,
  onClick,
}: {
  submitting: boolean;
  allTouched: boolean;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <div className="flex flex-col items-center">
      <button
        type="button"
        disabled={submitting}
        onClick={onClick}
        className="group relative inline-flex items-center gap-2 rounded-full px-8 py-4 text-base font-medium text-white transition duration-200 hover:-translate-y-0.5 disabled:cursor-wait disabled:opacity-80"
        style={{
          background:
            "linear-gradient(135deg, #ea580c 0%, #f97316 50%, #fb923c 100%)",
          boxShadow:
            "0 10px 32px -8px rgba(249,115,22,0.55), inset 0 1px 0 rgba(255,255,255,0.20)",
        }}
      >
        <SparkleIcon />
        <span>
          {submitting
            ? "Reading your wheel…"
            : allTouched
              ? "Get my personalized reflection"
              : "Reflect on what I've scored"}
        </span>
        <span
          aria-hidden
          className="transition-transform group-hover:translate-x-0.5"
        >
          →
        </span>
      </button>
      <p className="mt-3 text-xs text-neutral-500">
        Powered by your honest answers — takes about 5 seconds.
      </p>
    </div>
  );
}

function SparkleIcon(): React.JSX.Element {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
      style={{
        animation: "wheel-cta-sparkle 2400ms ease-in-out infinite",
      }}
    >
      <path d="M12 2l1.5 5L18 8l-4.5 1L12 14l-1.5-5L6 8l4.5-1L12 2zM19 13l.8 2.4L22 16l-2.2.6L19 19l-.8-2.4L16 16l2.2-.6L19 13zM5 14l.6 1.8L7 16l-1.4.4L5 18l-.6-1.6L3 16l1.4-.2L5 14z" />
      <style>{`@keyframes wheel-cta-sparkle { 0%,100% { opacity: 0.7 } 50% { opacity: 1 } }`}</style>
    </svg>
  );
}
